import { SchemaAttributes } from '@/Data/typesV2'

// `_id` is not a schema attribute — it is a synthetic mapping option used to
// look up existing records for upsert. It must be excluded from schema-driven
// validation (mandatory/enum checks) and never written as an attribute.
export const ID_ATTR_KEY = '_id'

// `email` IS a real schema attribute (unlike the synthetic `_id`), so it is still
// written as an attribute; this constant only marks it as the alternate upsert/match key.
export const EMAIL_ATTR_KEY = 'email'

type AttributeLabel = { value: string | boolean; label: string }

/** A row that failed to import, with a human-readable reason (surfaced in the export). */
// Why the row failed, as a translatable code rather than a baked English
// string — the reason is written into the user-facing failed-imports export,
// so it is resolved through `t()` at the download site (see ImportStep).
export type FailedReason =
  | { code: 'ambiguousEmail'; count: number }
  | { code: 'recordChanged' }
  | { code: 'unknown' }

export interface FailedImportRow {
  values: string[]
  reason: FailedReason
}

export interface CsvRowIssueErrorDetails {
  missingFields: string[]
  invalidEnums: Array<{ attrKey: string; rawValue: string }>
}

export type EnumLookupEntry = {
  type: 'enum' | 'enumarray'
  map: Map<string, string>
}

export function buildEnumLookups(
  schemaAttributes: SchemaAttributes,
): Record<string, EnumLookupEntry> {
  const result: Record<string, EnumLookupEntry> = {}
  for (const [key, attr] of Object.entries(schemaAttributes)) {
    if (attr.type === 'enum' || attr.type === 'enumarray') {
      const map = new Map<string, string>()
      for (const ev of attr.enumValues ?? []) {
        map.set(ev.title.trim().toLowerCase(), ev.value)
        map.set(ev.value.trim().toLowerCase(), ev.value)
      }
      result[key] = { type: attr.type, map }
    }
  }
  return result
}

export function resolveEnum(
  map: Map<string, string>,
  rawValue: string,
): string | null {
  return map.get(rawValue.trim().toLowerCase()) ?? null
}

export function resolveEnumArray(
  map: Map<string, string>,
  rawValue: string,
): string[] | null {
  const items = rawValue
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
  const resolved: string[] = []
  for (const item of items) {
    const r = map.get(item.toLowerCase())
    if (r === undefined) return null
    resolved.push(r)
  }
  return resolved
}

export type CsvImportContext = 'list' | 'overview'

export type AnalysisBanner =
  | { kind: 'none' }
  | {
      kind: 'error' | 'warning' | 'info'
      messageKey: string
      defaultText: string
      missingLabels?: string[]
    }

export function getAnalysisBanner({
  matchByEmail,
  isIdMapped,
  unmappedMandatoryLabels,
  context,
}: {
  matchByEmail: boolean
  isIdMapped: boolean
  unmappedMandatoryLabels: string[]
  context: CsvImportContext
}): AnalysisBanner {
  if (unmappedMandatoryLabels.length > 0) {
    return {
      kind: 'error',
      messageKey:
        context === 'overview'
          ? 'components.csv.analysis.missingMandatoryOverview'
          : 'components.csv.analysis.missingMandatoryFields',
      defaultText: 'Import cannot proceed — required fields are missing.',
      missingLabels: unmappedMandatoryLabels,
    }
  }

  if (matchByEmail) {
    return {
      kind: 'info',
      messageKey: 'components.csv.analysis.emailMapped',
      defaultText:
        'Rows whose email matches exactly a single record will be updated instead of created.',
    }
  }

  if (isIdMapped) {
    return {
      kind: 'info',
      messageKey: 'components.csv.analysis.idMapped',
      defaultText:
        'Rows whose ID matches an existing record will be updated instead of created.',
    }
  }

  return { kind: 'none' }
}

export function buildErrorText(
  errorDetails: CsvRowIssueErrorDetails,
  attributes: AttributeLabel[],
): string {
  const labelFor = (attrKey: string) =>
    attributes.find((a) => a.value === attrKey)?.label ?? attrKey

  const parts: string[] = []

  for (const attrKey of errorDetails.missingFields) {
    parts.push(`"${labelFor(attrKey)}" is empty`)
  }
  for (const { attrKey, rawValue } of errorDetails.invalidEnums) {
    parts.push(`"${labelFor(attrKey)}": '${rawValue}' is not a valid option`)
  }

  return parts.join('; ')
}
